import { useUploadStore } from '@/store/uploadStore';
import * as uploadService from '@/api/services/upload.service';
import type { UploadItem } from '@/upload/uploadTypes';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_CONCURRENT = 10;
const CHUNK_SIZE = 5 * 1024 * 1024; // 2 MB chunks
const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 1_000; // 1 second

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute SHA-256 of a File, returning hex string. */
async function hashFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Sleep for ms with abort support. */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

// ---------------------------------------------------------------------------
// Upload Manager (singleton)
// ---------------------------------------------------------------------------

class UploadManager {
  private activeCount = 0;
  private abortController: AbortController | null = null;
  private processing = false;

  private get store() {
    return useUploadStore.getState();
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Enqueue files and begin uploading.
   * Creates a session on the server, then starts processing.
   */
  async start(eventId: string, files: File[]): Promise<void> {
    const store = this.store;

    // Enqueue all files first.
    store.enqueueFiles(files);

    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

    // Create server session.
    const session = await uploadService.createUploadSession(
      eventId,
      files.length,
      totalBytes,
    );

    store.startUpload(session.id, eventId);

    this.abortController = new AbortController();
    this.processing = true;

    this.fillSlots();
  }

  /** Pause all uploads. Active chunks finish but no new work starts. */
  pause(): void {
    this.processing = false;
    this.store.pauseUpload();
  }

  /** Resume after pause. */
  resume(): void {
    const store = this.store;
    if (store.status !== 'paused') return;

    store.resumeUpload();
    this.processing = true;
    this.fillSlots();
  }

  /** Retry all failed items. */
  retryFailed(): void {
    this.store.retryFailed();
    this.processing = true;
    this.fillSlots();
  }

  /** Cancel everything. Aborts in-flight XHRs. */
  cancel(): void {
    this.processing = false;
    this.abortController?.abort();
    this.abortController = null;
    this.activeCount = 0;
    this.store.reset();
  }

  // -----------------------------------------------------------------------
  // Internal: slot management
  // -----------------------------------------------------------------------

  /** Fill available concurrent slots from the pending queue. */
  private fillSlots(): void {
    if (!this.processing) return;

    while (this.activeCount < MAX_CONCURRENT) {
      const item = this.store.dequeueNext();
      if (!item) break;

      this.activeCount++;
      this.store.activateItem(item.id);
      this.processItem(item).finally(() => {
        this.activeCount--;
        this.store.deactivateItem(item.id);
        this.fillSlots(); // try to fill the freed slot
      });
    }
  }

  // -----------------------------------------------------------------------
  // Internal: per-file pipeline
  // -----------------------------------------------------------------------

  private async processItem(item: UploadItem): Promise<void> {
    const { sessionId } = this.store;
    if (!sessionId) {
      this.store.markItemFailed(item.id, 'No active session');
      return;
    }

    const signal = this.abortController?.signal;

    try {
      // 1. Hash -----------------------------------------------------------
      this.store.updateItemStatus(item.id, 'hashing');
      const hash = await hashFile(item.file);
      this.store.updateItemStatus(item.id, 'hashing', { hash });

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      // 2. Register -------------------------------------------------------
      this.store.updateItemStatus(item.id, 'registering');
      const totalChunks = Math.ceil(item.file.size / CHUNK_SIZE);

      const registered = await uploadService.registerFileUpload(sessionId, {
        original_filename: item.file.name,
        content_type: item.file.type || 'application/octet-stream',
        total_size: item.file.size,
        total_chunks: totalChunks,
        client_checksum: hash,
      });

      const uploadId = registered.id;
      this.store.updateItemStatus(item.id, 'uploading', { uploadId });

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      // 3. Upload chunks --------------------------------------------------
      await this.uploadChunks(item.id, uploadId, item.file, totalChunks, signal);

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      // 4. Complete -------------------------------------------------------
      this.store.updateItemStatus(item.id, 'completing');
      await uploadService.completeUpload(uploadId);

      this.store.markItemCompleted(item.id, item.file.size);
    } catch (err) {
      if (isAbortError(err)) {
        // Paused or cancelled -- don't mark as failed. Re-enqueue as pending.
        if (this.store.status === 'paused') {
          this.store.updateItemStatus(item.id, 'pending');
        }
        return;
      }

      const message = err instanceof Error ? err.message : 'Unknown upload error';
      this.store.markItemFailed(item.id, message);
    }
  }

  // -----------------------------------------------------------------------
  // Internal: chunked upload with retry
  // -----------------------------------------------------------------------

  private async uploadChunks(
    itemId: string,
    uploadId: string,
    file: File,
    totalChunks: number,
    signal?: AbortSignal,
  ): Promise<void> {
    let uploadedChunkBytes = 0;

    for (let i = 0; i < totalChunks; i++) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (!this.processing) throw new DOMException('Aborted', 'AbortError');

      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      await this.uploadChunkWithRetry(itemId, uploadId, i, chunk, file.size, uploadedChunkBytes, signal);

      uploadedChunkBytes += chunk.size;
    }
  }

  private async uploadChunkWithRetry(
    itemId: string,
    uploadId: string,
    chunkIndex: number,
    chunk: Blob,
    totalFileSize: number,
    uploadedSoFar: number,
    signal?: AbortSignal,
  ): Promise<void> {
    let attempt = 0;

    while (true) {
      try {
        await uploadService.uploadChunk(
          uploadId,
          chunkIndex,
          chunk,
          (loaded, _total) => {
            const totalDone = uploadedSoFar + loaded;
            const progress = Math.min(
              99, // cap at 99 until complete step succeeds
              Math.round((totalDone / totalFileSize) * 100),
            );
            this.store.updateItemProgress(itemId, progress, totalDone);
          },
          signal,
        );
        return; // success
      } catch (err) {
        if (isAbortError(err)) throw err;

        attempt++;
        if (attempt >= MAX_RETRIES) {
          throw err;
        }

        // Exponential backoff: 1s, 2s, 4s ...
        const delay = BASE_BACKOFF_MS * Math.pow(2, attempt - 1);
        await sleep(delay, signal);
      }
    }
  }
}

/** Singleton instance. Import this wherever you need to control uploads. */
export const uploadManager = new UploadManager();
